import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { EditableComponent } from '@adobe/aem-react-editable-components'
import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager'
import {
  FaqSubCategories as UIFoundationFaqSubCategories,
  FaqSubCategoriesDevice,
  FaqSubCategoriesFaqItem
} from '@dcxwam/dcx-wam-ui-foundations'
import { useExtractEditableProps } from '../../../../utils/aem/editor/extractEditableProps'
import { EditableComponentPropsType } from '../../../../utils/prop-types'
import {
  withErrorBoundary,
  ErrorFallback
} from '../../../functional/ErrorBoundary'
import { FaqsSubCategoriesProps, SubCategory } from './types'
import FaqsSubCategoriesEditConfig from './FaqsSubCategoriesEditConfig'
import { fetchArticlesByCategory } from '../common/FaqsFetch'
import {
  buildArticleUrl,
  buildCbxConfig,
  executeCategoryFaqs,
  extractArticlesFromResponse,
  replaceFaqsQuery
} from '../common/FaqsUtils'
import {
  buildFaqCollectionListItems,
  generateItemListJsonLD
} from '../common/faq-schema.utils'
import type { Article, FaqsSDKConfig } from '../../../../services/faqs/faqs-sdk'
import { accentsTidy } from '../../StoreDetails/utils'
import { setBreadcrumbTitle } from '../../../../utils/breadcrumb-context'

const findSubCategoryBySlug = (
  subs: SubCategory[],
  pageSlug: string
): SubCategory | undefined => {
  const normalizedSlug = pageSlug.toLowerCase()
  const sorted = [...subs].sort(
    (a, b) =>
      accentsTidy(b.categoryName).length - accentsTidy(a.categoryName).length
  )
  return sorted.find((sub) => {
    const subSlug = accentsTidy(sub.categoryName).toLowerCase()
    return normalizedSlug.includes(subSlug)
  })
}

interface FetchArticlesOptions {
  sdkConfig: FaqsSDKConfig
  subs: SubCategory[]
  selectedSubCategoryId?: string
}

const fetchSubCategoryArticles = async (
  options: FetchArticlesOptions
): Promise<Article[]> => {
  const { sdkConfig, subs, selectedSubCategoryId } = options
  if (selectedSubCategoryId) {
    const data = await fetchArticlesByCategory(sdkConfig, selectedSubCategoryId)
    return extractArticlesFromResponse(data)
  }
  const allData = await Promise.all(
    subs.map((sub) => fetchArticlesByCategory(sdkConfig, sub.categoryId))
  )
  return allData.flatMap((data) => extractArticlesFromResponse(data))
}

const FaqsSubCategories: FC<
  EditableComponentPropsType<FaqsSubCategoriesProps>
> = (props) => {
  const { model } = props
  const {
    categorySlug,
    categoryWithProduct,
    faqConfig,
    initialSubCategories,
    initialArticles,
    initialCategoryName,
    initialActiveDeviceId,
    pageUrl,
    ...rest
  } = model || {}
  const editableProps = useExtractEditableProps(props, rest)

  const [subCategories, setSubCategories] = useState<SubCategory[]>(
    initialSubCategories ?? []
  )
  const [articles, setArticles] = useState<Article[]>(initialArticles ?? [])
  const [activeDeviceId, setActiveDeviceId] = useState<string>(
    initialActiveDeviceId ?? ''
  )
  const [categoryName, setCategoryName] = useState<string>(
    initialCategoryName ?? ''
  )
  const [loading, setLoading] = useState(!initialSubCategories?.length)

  const isDeviceView = useMemo(() => {
    if (!categorySlug || !categoryWithProduct) {
      return false
    }
    return categoryWithProduct.includes(categorySlug)
  }, [categorySlug, categoryWithProduct])

  const origin = useMemo((): string => {
    if (pageUrl) {
      try {
        return new URL(pageUrl).origin
      } catch {
        return ''
      }
    }
    return typeof window !== 'undefined' ? window.location.origin : ''
  }, [pageUrl])

  const getSdkConfig = useCallback(
    (): FaqsSDKConfig => buildCbxConfig() as FaqsSDKConfig,
    []
  )

  const getCurrentPageSlug = useCallback((): string => {
    const pathname =
      typeof window !== 'undefined' ? window.location.pathname : (pageUrl ?? '')
    return (
      pathname
        .replace(/\.html$/, '')
        .replace(/\/$/, '')
        .split('/')
        .pop() ?? ''
    )
  }, [pageUrl])

  const findSubCategoryFromUrl = useCallback(
    (subs: SubCategory[]): SubCategory | undefined => {
      const currentSlug = getCurrentPageSlug()
      return findSubCategoryBySlug(subs, currentSlug)
    },
    [getCurrentPageSlug]
  )

  const buildDevicePageUrl = useCallback(
    (sub: SubCategory): string => {
      const rawPath =
        typeof window !== 'undefined'
          ? window.location.pathname
          : (pageUrl ?? '').replace(/^https?:\/\/[^/]+/, '')
      const currentPath = rawPath.replace(/\.html$/, '').replace(/\/$/, '')
      const basePath = currentPath.split('/').slice(0, -1).join('/')
      const catName = accentsTidy(categoryName)
      const subName = accentsTidy(sub.categoryName)
      return `${basePath}/${catName}-${subName}-${categorySlug}.html`
    },
    [categoryName, categorySlug, pageUrl]
  )

  useEffect(() => {
    if (
      isDeviceView &&
      initialSubCategories?.length &&
      initialActiveDeviceId &&
      initialCategoryName
    ) {
      const activeSub = initialSubCategories.find(
        (s) => s.categoryId === initialActiveDeviceId
      )
      if (activeSub && typeof window !== 'undefined') {
        const currentPath = window.location.pathname
          .replace(/\.html$/, '')
          .replace(/\/$/, '')
        const basePath = currentPath.split('/').slice(0, -1).join('/')
        const catName = accentsTidy(initialCategoryName)
        const subName = accentsTidy(activeSub.categoryName)
        const newUrl = `${basePath}/${catName}-${subName}-${categorySlug}.html`
        window.history.replaceState({}, '', newUrl)
      }
    }
  }, [])

  useEffect(() => {
    if (initialCategoryName) {
      setBreadcrumbTitle(initialCategoryName)
    }
    if (!categorySlug || initialSubCategories?.length) {
      setLoading(false)
      return
    }

    const loadData = async () => {
      setLoading(true)
      const sdkConfig = getSdkConfig()
      const { subs, nodes } = await executeCategoryFaqs(categorySlug, faqConfig)
      if (subs?.length === 0) {
        setLoading(false)
        return
      }
      if (subs && nodes) {
        setSubCategories(subs)
        const parentCategoryName = nodes[0]?.categoryName ?? ''
        setCategoryName(parentCategoryName)
        setBreadcrumbTitle(parentCategoryName)

        if (isDeviceView) {
          const urlMatch = findSubCategoryFromUrl(subs)
          const selectedSub = urlMatch ?? subs[0]
          setActiveDeviceId(selectedSub.categoryId)
          const fetchedArticles = await fetchSubCategoryArticles({
            sdkConfig,
            subs,
            selectedSubCategoryId: selectedSub.categoryId
          })
          setArticles(fetchedArticles)

          const currentPath = window.location.pathname
            .replace(/\.html$/, '')
            .replace(/\/$/, '')
          const basePath = currentPath.split('/').slice(0, -1).join('/')
          const catName = accentsTidy(parentCategoryName)
          const subName = accentsTidy(selectedSub.categoryName)
          const newUrl = `${basePath}/${catName}-${subName}-${categorySlug}.html`
          window.history.replaceState({}, '', newUrl)
        } else {
          const fetchedArticles = await fetchSubCategoryArticles({
            sdkConfig,
            subs
          })
          setArticles(fetchedArticles)
        }
      }
      setLoading(false)
    }
    loadData()
  }, [
    categorySlug,
    isDeviceView,
    getSdkConfig,
    findSubCategoryFromUrl,
    initialSubCategories
  ])

  const handleDeviceClick = useCallback(
    async (deviceId: string) => {
      setActiveDeviceId(deviceId)
      const sdkConfig = getSdkConfig()
      const data = await fetchArticlesByCategory(sdkConfig, deviceId)
      const fetchedArticles = extractArticlesFromResponse(data)
      setArticles(fetchedArticles)

      const clickedSub = subCategories.find((s) => s.categoryId === deviceId)
      if (clickedSub) {
        const newUrl = buildDevicePageUrl(clickedSub)
        window.history.replaceState({}, '', newUrl)
      }
    },
    [getSdkConfig, subCategories, buildDevicePageUrl]
  )

  const buildFaqItemUrl = useCallback(
    (article: Article): string => {
      const faqArticlePagePath = faqConfig?.faqArticlePagePath ?? ''
      const currentSlug = getCurrentPageSlug()
      const articleSlug = `${accentsTidy(article.articleTitle)}-${article.articleId}`
      const relativePath = buildArticleUrl(
        faqArticlePagePath,
        `${currentSlug}/${articleSlug}`
      )
      return origin ? `${origin}${relativePath}` : relativePath
    },
    [faqConfig, getCurrentPageSlug, origin]
  )

  const handleFaqItemClick = useCallback((item: FaqSubCategoriesFaqItem) => {
    if (item.linkUrl) {
      window.location.href = item.linkUrl
    }
  }, [])

  const devices: FaqSubCategoriesDevice[] = useMemo(() => {
    if (!isDeviceView || subCategories.length === 0) {
      return []
    }

    const imagesPath = faqConfig?.faqSubCategoryImagesPath ?? ''

    return subCategories.map((sub) => ({
      id: sub.categoryId,
      label: sub.categoryName,
      imageSrc: imagesPath ? `${imagesPath}/${sub.categoryId}.png` : '',
      imageAlt: sub.categoryName,
      href: buildDevicePageUrl(sub),
      faqItems:
        sub.categoryId === activeDeviceId
          ? articles.map((article) => ({
              id: String(article.articleId),
              label: article.articleTitle,
              linkUrl: buildFaqItemUrl(article)
            }))
          : []
    }))
  }, [
    isDeviceView,
    subCategories,
    activeDeviceId,
    articles,
    buildFaqItemUrl,
    buildDevicePageUrl,
    faqConfig?.faqSubCategoryImagesPath
  ])

  const faqItems: FaqSubCategoriesFaqItem[] = useMemo(() => {
    if (isDeviceView || articles.length === 0) {
      return []
    }

    return articles.map((article) => {
      const categories = article.categories ?? []
      const lastCategory = categories[categories.length - 1]
      return {
        id: String(article.articleId),
        label: article.articleTitle,
        linkUrl: buildFaqItemUrl(article),
        categoryName: lastCategory?.categoryName
      }
    })
  }, [isDeviceView, articles, buildFaqItemUrl])

  if (loading || (articles.length === 0 && subCategories.length === 0)) {
    if (AuthoringUtils.isInEditor()) {
      return (
        <EditableComponent {...editableProps}>
          {FaqsSubCategoriesEditConfig.configLabel}
        </EditableComponent>
      )
    }
    return null
  }

  const getTitle = (): string => {
    if (isDeviceView) {
      return faqConfig?.deviceSelectionTitle ?? ''
    }
    if (articles.length > 0) {
      const categories = articles[0].categories ?? []
      const lastCategory = categories[categories.length - 1]
      return lastCategory?.categoryName ?? ''
    }
    return subCategories[0]?.categoryName ?? ''
  }

  const renderContent = () => {
    if (FaqsSubCategoriesEditConfig.isEmpty(model)) {
      return FaqsSubCategoriesEditConfig.configLabel
    }
    if (isDeviceView) {
      return (
        <UIFoundationFaqSubCategories
          title={getTitle()}
          devices={devices}
          activeDeviceId={activeDeviceId || devices[0]?.id}
          onDeviceClick={handleDeviceClick}
          onFaqItemClick={handleFaqItemClick}
        />
      )
    }
    return (
      <UIFoundationFaqSubCategories
        title={getTitle()}
        faqItems={faqItems}
        onFaqItemClick={handleFaqItemClick}
      />
    )
  }

  return (
    <EditableComponent {...editableProps}>{renderContent()}</EditableComponent>
  )
}

// @ts-expect-error Property 'getServerSideProps' does not exist
// eslint-disable-next-line @typescript-eslint/no-explicit-any
FaqsSubCategories.getServerSideProps = async (props: Record<string, any>) => {
  const { categorySlug, categoryWithProduct, faqConfig, ...rest } = props

  if (!categorySlug) {
    return { ...rest, categorySlug, categoryWithProduct, faqConfig }
  }

  try {
    const { subs, nodes } = await executeCategoryFaqs(
      categorySlug,
      faqConfig,
      true
    )

    if (!subs?.length) {
      return { ...rest, categorySlug, categoryWithProduct, faqConfig }
    }

    const initialCategoryName = nodes?.[0]?.categoryName ?? ''
    const isDeviceView = categoryWithProduct?.includes(categorySlug) ?? false
    const sdkConfig = buildCbxConfig(undefined, true) as FaqsSDKConfig

    let initialArticles: Article[] = []
    let initialActiveDeviceId = ''
    try {
      if (isDeviceView) {
        const pageSlug =
          (rest.pageUrl ?? '')
            .replace(/\.html$/, '')
            .replace(/\/$/, '')
            .split('/')
            .pop()
            ?.toLowerCase() ?? ''

        const matchedSub = findSubCategoryBySlug(subs, pageSlug)
        const selectedSub = matchedSub ?? subs[0]
        initialActiveDeviceId = selectedSub.categoryId

        initialArticles = await fetchSubCategoryArticles({
          sdkConfig,
          subs,
          selectedSubCategoryId: selectedSub.categoryId
        })
      } else {
        initialArticles = await fetchSubCategoryArticles({
          sdkConfig,
          subs
        })
      }
    } catch {
      initialArticles = []
    }

    return {
      ...rest,
      categorySlug,
      categoryWithProduct,
      faqConfig,
      initialSubCategories: subs,
      initialArticles,
      initialCategoryName,
      initialActiveDeviceId
    }
  } catch {
    // Gracefully fall through to CSR
    return { ...rest, categorySlug, categoryWithProduct, faqConfig }
  }
}

// @ts-expect-error Property 'getHeadData' does not exist
// eslint-disable-next-line @typescript-eslint/no-explicit-any
FaqsSubCategories.getHeadData = async (props: Record<string, any>) => {
  const {
    seoTitle,
    seoDescription,
    categorySlug,
    initialCategoryName,
    initialArticles,
    faqConfig,
    pageUrl
  } = props ?? {}
  if (!categorySlug) {
    return {
      status: 200,
      seoTitle: replaceFaqsQuery(seoTitle, null),
      seoDescription: replaceFaqsQuery(seoDescription, null)
    }
  }

  const categoryName = initialCategoryName ?? ''
  if (!categoryName) {
    return {
      status: 200,
      seoTitle: replaceFaqsQuery(seoTitle, null),
      seoDescription: replaceFaqsQuery(seoDescription, null)
    }
  }

  const seoTitleUpdate = replaceFaqsQuery(seoTitle, categoryName)
  const seoDescriptionUpdate = replaceFaqsQuery(seoDescription, categoryName)

  let jsonLD = null
  const enableCollectionSchema = !!faqConfig?.enableFaqCollectionListSchema
  if (enableCollectionSchema && initialArticles?.length) {
    const faqArticlePagePath = faqConfig?.faqArticlePagePath ?? ''
    const categoryPageSlug =
      (pageUrl ?? '')
        .replace(/\.html$/, '')
        .replace(/\/$/, '')
        .split('/')
        .pop() ?? ''
    const pageOrigin = pageUrl ? new URL(pageUrl).origin : ''
    const items = buildFaqCollectionListItems({
      articles: initialArticles,
      faqArticlePagePath,
      categorySlug: categoryPageSlug,
      baseOrigin: pageOrigin
    })
    const schemaNameTemplate = faqConfig?.faqCollectionListSchemaName ?? ''
    const schemaName = schemaNameTemplate.replace(
      '{categoryName}',
      categoryName
    )
    jsonLD = generateItemListJsonLD({
      name: schemaName,
      url: pageUrl ?? '',
      items
    })
  }

  return {
    status: 200,
    seoTitle: seoTitleUpdate,
    seoDescription: seoDescriptionUpdate,
    dynamicPageTitle: categoryName,
    ...(jsonLD && { jsonLD })
  }
}

const FaqsSubCategoriesWithErrorBoundary = withErrorBoundary(
  FaqsSubCategories,
  {
    componentName: 'FaqsSubCategories',
    fallback: <ErrorFallback componentName="FaqsSubCategories" />
  }
)

export default FaqsSubCategoriesWithErrorBoundary
